'use client'

import { isDefined } from '@morpho-org/morpho-ts'
import { cn } from '@morpho-org/ui-v6/utils'
import { Button } from '@repo/ui'
import { Arrow } from '@repo/ui/icons'

import { Skeleton } from '../server/Skeleton'

export interface CursorPaginationProps {
  previousPage: () => void
  nextPage: () => void
  canPreviousPage: boolean
  canNextPage: boolean
  loading: boolean
  pageIndex?: number
  /** The number of pages, when the endpoint returns a total; shown as "of N" next to the page. */
  pageCount?: number
  error?: Error | null
  onPrefetchNext?: () => void
  className?: string
}

/**
 * Prev/Next pagination for cursor-based endpoints.
 *
 * The Morpho consumer API exposes activity endpoints (vault/market
 * transactions and liquidations) as cursor-paginated feeds: each response
 * carries an opaque `endCursor` and a `hasNextPage` flag, but no total count
 * and no random-access. The "of N" suffix and the jump-to-page input from
 * the offset `Pagination` component are therefore dropped, but the current
 * page index is still rendered as a read-only position indicator when
 * supplied. An endpoint that does return a total can pass `pageCount` to show
 * "of N" after it.
 */
export function CursorPagination({
  previousPage,
  nextPage,
  canPreviousPage,
  canNextPage,
  loading,
  pageIndex,
  pageCount,
  error,
  className,
  onPrefetchNext
}: CursorPaginationProps) {
  if (error) return null

  if (loading && !canPreviousPage && !canNextPage) {
    return (
      <div className={cn('gap-sm flex flex-row items-center justify-center', className)}>
        <Skeleton className="size-8 rounded-full" />
        <Skeleton className="size-8 rounded-full" />
      </div>
    )
  }

  if (!canPreviousPage && !canNextPage) return null

  return (
    <div className={cn('gap-sm flex flex-row items-center justify-center', className)}>
      <Button
        variant="secondary"
        size="icon"
        onClick={previousPage}
        disabled={!canPreviousPage || loading}
        aria-label="Previous page"
      >
        <Arrow className="rotate-180" />
      </Button>
      {isDefined(pageIndex) && (
        <span className="px-sm py-2xs bg-muted text-foreground flex cursor-default items-center gap-[2.5px] rounded-[20px] text-[10px] leading-none">
          <span>{pageIndex + 1}</span>
          {isDefined(pageCount) && <span>of {pageCount}</span>}
        </span>
      )}
      <Button
        variant="secondary"
        size="icon"
        onClick={nextPage}
        disabled={!canNextPage || loading}
        onMouseEnter={canNextPage && !loading ? onPrefetchNext : undefined}
        aria-label="Next page"
      >
        <Arrow />
      </Button>
    </div>
  )
}
